/**
 * Assign a stranded charge to the encounter it was really for (gdn-3106,
 * consuming gdn-3023's server verb).
 *
 * A biller collects for a visit by raising an ad-hoc charge — often before
 * the claim comes back, so the encounter has no patient responsibility
 * yet. When the ERA lands it creates the real responsibility and nothing
 * connects the two: the encounter reads unpaid forever while the money
 * sits against a charge that only stood in for it. Production holds 205
 * such charges, $20,903.79.
 *
 * ONE call, deliberately. The repair was always possible as reverse-then-
 * allocate, and that shape is unofferable: asking someone to *reverse a
 * paid charge* as the opening move in filing a payment correctly reads as
 * destructive, and between the two steps the patient owes MORE than
 * before — the charge is gone, the credit has not landed. An interruption
 * there leaves the account further from the truth than the bug did. The
 * composed endpoint is what makes this safe to offer, so the UI must
 * never re-split it.
 *
 * The target is chosen, never inferred. `target_encounter_id` is required
 * server-side for the same reason the composer refuses to guess
 * (gdn-3086): on the account that prompted this work, two charges name
 * the same visit in their free text, and whether the second is that
 * visit's deductible or an over-collection is a judgement only the biller
 * can make.
 *
 * Target-side refusals (settled, on hold, owes less than the charge) are
 * decided against the ledger at write time, so they are not knowable here
 * — the server's message is rendered verbatim rather than paraphrased.
 */

import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';

import { useAssignChargeToEncounterMutation } from '../../store/v2-api';

import { type CreditTarget, formatCents } from './rows';

export interface AssignChargeDialogProps {
  patientId: string;
  /** The stranded charge — id to act on, amount for the copy. */
  charge: { chargeLineId: string; amountCents: number; label: string };
  /** Encounters that can receive the money: reconciled, owing, not on
   * hold. Ordered most recent first by the caller — order, never
   * selection. */
  targets: CreditTarget[];
  /** Subject-language singular ("patient" | "client") — a prop rather
   * than a hook so the component stays presentational (no redux in
   * Storybook), matching PatientBalanceTable's subjectCap. */
  subject?: string;
  onClose: () => void;
  /** Fired at confirm-click (the interaction), before the POST. */
  onSubmitted?: (payload: {
    target_encounter_id: string;
    has_note: boolean;
    /** The split as previewed at confirm time. Client-derived on purpose:
     * this is an interaction event, so it records what the biller agreed
     * to, not what the ledger later did. */
    assigned_cents: number;
    credited_cents: number;
  }) => void;
  /** Fired once the server accepts the repair, carrying the split the
   * ledger actually made. Trusted over the dialog's preview: the room it
   * capped against is the ledger's, not the reconciliation read model's,
   * and only the server knows what finally moved. */
  onAssigned?: (payload: {
    amount_cents: number;
    assigned_cents: number;
    credited_cents: number;
  }) => void;
}

export const AssignChargeDialog: React.FC<AssignChargeDialogProps> = ({
  patientId,
  charge,
  targets,
  subject = 'patient',
  onClose,
  onSubmitted,
  onAssigned,
}) => {
  const [targetId, setTargetId] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [errorText, setErrorText] = useState<string | null>(null);
  const [assignCharge, { isLoading }] = useAssignChargeToEncounterMutation();
  const tracking = useEventTracking();

  const target = targets.find(candidate => candidate.encounterId === targetId);

  // What the repair will do to the selected target, previewed from the two
  // numbers already on screen (gdn-3125). A stand-in copay is estimated
  // before the claim comes back, so "larger than the adjudicated
  // responsibility" is the ordinary case, not the edge — the server caps
  // at the target's room and banks the rest as account credit rather than
  // refusing. Preview only: the success path trusts the server's split,
  // which is the authority on what actually moved.
  const assignedPreviewCents = target
    ? Math.min(charge.amountCents, target.remainingDueCents)
    : 0;
  const creditedPreviewCents = charge.amountCents - assignedPreviewCents;
  const shortfallCents = target
    ? target.remainingDueCents - assignedPreviewCents
    : 0;

  const handleConfirm = async () => {
    if (!target) return;
    setErrorText(null);
    onSubmitted?.({
      target_encounter_id: target.encounterId,
      has_note: note.trim().length > 0,
      assigned_cents: assignedPreviewCents,
      credited_cents: creditedPreviewCents,
    });
    try {
      const result = await assignCharge({
        patientId,
        assignChargeToEncounterRequest: {
          charge_line_id: charge.chargeLineId,
          target_encounter_id: target.encounterId,
          note: note.trim() || null,
        },
      }).unwrap();
      onAssigned?.({
        amount_cents: result.amount_cents,
        assigned_cents: result.assigned_cents,
        credited_cents: result.credited_cents,
      });
      onClose();
    } catch (error) {
      // The server's refusals are specific and actionable ("Target
      // encounter has no outstanding balance", "Charge is larger than the
      // target encounter's outstanding balance"). Render them verbatim —
      // a paraphrase would lose which of them happened.
      const message = (error as { data?: { message?: string } })?.data?.message;
      // A refused repair is the measurement this verb most needs after
      // the click itself: if billers keep hitting "target encounter has
      // no outstanding balance", the picker is offering wrong targets and
      // the 205 stay stranded for a reason no outcome event would show.
      // The server's refusals are a fixed set of billing-state strings —
      // no PHI — and ERROR_OCCURRED is not a dropped `_failed` suffix.
      tracking.error.track(
        message ?? 'assignment_failed',
        'charge_assignment',
        { patient_id: patientId },
      );
      setErrorText(
        message ??
          'The charge could not be assigned. Nothing was changed — try again.',
      );
    }
  };

  return (
    <Dialog open onClose={isLoading ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: '1rem', fontWeight: 700 }}>
        Assign {formatCents(charge.amountCents)} to an encounter
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          “{charge.label}” was collected at the account level. Assigning it
          files that money against the encounter it actually paid for.
        </Typography>

        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Which encounter did this pay for?
        </Typography>
        {targets.length === 0 ? (
          <Alert severity="info" sx={{ fontSize: '0.8125rem' }}>
            No encounter is currently owing, so there is nothing to assign
            this charge to.
          </Alert>
        ) : (
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            {targets.map(candidate => {
              const selected = candidate.encounterId === targetId;
              return (
                <Box
                  key={candidate.encounterId}
                  // The server's refusals name a specific target ("owes
                  // less than the charge"), so they must not outlive the
                  // selection they describe.
                  onClick={() => {
                    setTargetId(candidate.encounterId);
                    setErrorText(null);
                  }}
                  onKeyDown={event => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setTargetId(candidate.encounterId);
                      setErrorText(null);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-pressed={selected}
                  aria-label={`Assign the charge to ${candidate.label}`}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 2,
                    px: 2,
                    py: 1.25,
                    cursor: 'pointer',
                    bgcolor: selected ? 'action.selected' : 'background.paper',
                    '&:not(:first-of-type)': {
                      borderTop: '1px solid',
                      borderColor: 'divider',
                    },
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {candidate.label}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary', display: 'block' }}
                    >
                      {candidate.date ?? 'Undated'}
                    </Typography>
                  </Box>
                  {/* Context, not a filter: whether the encounter has room
                      for this charge is decided against the ledger at write
                      time, so the server is the authority. */}
                  <Typography
                    variant="caption"
                    sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}
                  >
                    {formatCents(candidate.remainingDueCents)} owed
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}

        <TextField
          label="Note (optional)"
          value={note}
          onChange={event => setNote(event.target.value)}
          size="small"
          fullWidth
          multiline
          minRows={2}
        />

        {target && (
          // What actually happens, stated plainly. The account charge is
          // voided and the money lands on the encounter — the patient's
          // TOTAL balance does not move, only where it is filed. Said
          // explicitly because the gdn-3064 void dialog once claimed a
          // balance change that did not happen.
          <Alert severity="info" sx={{ fontSize: '0.8125rem' }}>
            {creditedPreviewCents > 0
              ? `${formatCents(assignedPreviewCents)} settles ${target.label}, and the remaining ${formatCents(creditedPreviewCents)} stays as account credit you can apply to another encounter.`
              : shortfallCents > 0
                ? `${formatCents(assignedPreviewCents)} moves onto ${target.label}, leaving ${formatCents(shortfallCents)} still owed there.`
                : `${formatCents(assignedPreviewCents)} settles ${target.label}.`}{' '}
            {/* The stand-in is voided and its payment released as account
                credit, so the money stops being locked against a charge
                that was only standing in — which is exactly why the
                headline balance moves. It falls by the stand-in's full
                amount in every case: before, charge and payment cancelled
                to nothing while the encounter still owed; after, the
                payment offsets real responsibility. */}
            Voiding the stand-in frees its payment, so what the {subject} owes
            drops by {formatCents(charge.amountCents)}.
          </Alert>
        )}
        {errorText && (
          <Alert severity="error" sx={{ fontSize: '0.8125rem' }}>
            {errorText}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          disabled={isLoading}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!target || isLoading}
          onClick={() => void handleConfirm()}
          startIcon={isLoading ? <CircularProgress size={14} /> : undefined}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {isLoading ? 'Assigning…' : 'Assign charge'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
